import { createContext, useContext, useCallback } from 'react';

export type RangeValue = { min: number | null; max: number | null };
export type FilterValue = string | string[] | RangeValue | null;

export interface FiltersState {
  filters: Record<string, FilterValue>;
}

export interface FiltersContextValue {
  filters: Record<string, FilterValue>;
  setFilter: (name: string, value: FilterValue) => void;
  resetFilters: () => void;
}

export const FiltersContext = createContext<FiltersContextValue | null>(null);

export function useFilters(): FiltersContextValue {
  const context = useContext(FiltersContext);

  if (!context) {
    throw new Error('useFilters must be used within a FiltersProvider');
  }

  return context;
}

/**
 * Access a single filter, returning [value, setValue] like useState.
 */
export function useFilter(name: string): [FilterValue, (value: FilterValue) => void] {
  const { filters, setFilter } = useFilters();

  const value = filters[name] ?? null;
  const setValue = useCallback(
    (newValue: FilterValue) => setFilter(name, newValue),
    [name, setFilter]
  );

  return [value, setValue];
}
